import React, {FC, useState} from "react"

import {Body, H1, H3} from "@irati/ui/src/Typography"
import Spacing from "@irati/ui/src/Spacing"
import Button from "@irati/ui/src/Button"
import useBenefitsContentQuery from "@irati/benefits/hooks/benefitsContent"
import Loading from "@irati/ui/src/Loading"
import theme from "@irati/ui/src/global/theme"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import CircledConnector from "@irati/ui/src/CircledConnector"
import Link from "@irati/ui/src/Link"
import ExpandingList from "@irati/ui/src/ExpandingList"
import assertionService from "@irati/auth/services/assertions"
import {GenerateAssertionProvider, VivupBenefitType} from "@irati/types"
import {HasPermissionTo} from "@irati/utils/permissions"
import tracking from "@irati-tracking/benefits"

import {BannerImage, BannerLayout, Gradient, MobileCta, ResponsiveCard, ResponsiveMargin} from "./styled"
import BenefitModal from "./BenefitModal"

interface BenefitPageProps {
  benefit: "ctw" | "he" | "alp"
}

const BenefitPage: FC<BenefitPageProps> = ({benefit}) => {
  const [loadSSO, setLoadSSO] = useState<Promise<any>>()
  const {load, data} = useBenefitsContentQuery({benefit}, {})
  
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoadingSecondState, setIsLoadingSecondState] = useState(false)

  
  const openModal = () => {
    setIsModalOpen(true);
    setIsLoadingSecondState(true)

    setTimeout(() => {
      onClick("explore_benefit")
      setTimeout(() => {
        setIsLoadingSecondState(false)
      }, 2000)
    }, 3000)
  }
  
  const closeModal = () => {
    setIsModalOpen(false)
    setIsLoadingSecondState(false)
  }

  const faqListItems =
    data?.faq?.content?.map(({q = "", a = ""}) => {
      return {title: q, description: a}
    }) || []

  const onClick = (type: string) => {
    tracking.clickBenefitsCta("benefit_landing_page", type, data?.uuid!)
    const promise = assertionService.generateAssertion({
      provider: GenerateAssertionProvider.ServiceProviderVivup,
      metadata: {vivup: {benefitType: getBenefitType(benefit)}}
    })
    setLoadSSO(promise)
    promise.then(({response, acs}) => {
      const form = document.createElement("form")
      form.method = "POST"
      form.action = acs
      form.target = "_blank"
      const responseInput = document.createElement("input")
      responseInput.type = "hidden"
      responseInput.name = "SAMLResponse"
      responseInput.value = response
      form.appendChild(responseInput)
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    })
  }

  return (
    <ResponsiveMargin>
      <HasPermissionTo permissions={`benefits.${benefit}`}>
        <Loading load={load}>
          <Spacing hideOnDesktop marginBottom={3}>
            <H1>{data?.title}</H1>
            <Body marginTop={1}>{data?.description}</Body>
          </Spacing>
          <ResponsiveCard flush>
            <Gradient>
              <BannerLayout>
                <Spacing hideOnMobile>
                  <H1 color={theme.colors.greys.white}>{data?.title}</H1>
                  <Body color={theme.colors.greys.white} marginTop={2}>
                    {data?.description}
                  </Body>
                  <Button primary large onClick={openModal} marginTop={2} marginBottom={2} load={loadSSO}>
                    {data?.cta} <Icon icon={IconTypes.upRightArrow} marginLeft={2} size={16} />
                  </Button>
                </Spacing>
                <BannerImage src={data?.bannerImage} alt="Image" />
              </BannerLayout>
            </Gradient>
            <Spacing padding={[0, 4, 4, 4]}>
              {data?.howItWorks && (
                <>
                  <H3 marginTop={3} marginBottom={2}>
                    {data?.howItWorks?.title}
                  </H3>
                  <CircledConnector data={data?.howItWorks?.content || []} />
                </>
              )}
              {data?.why && (
                <>
                  <H3 marginBottom={2}>{data?.why?.title}</H3>
                  <ul style={{paddingLeft: theme.spacing.four}}>
                    {data?.why?.content?.map((item, i) => (
                      <li key={i}>
                        <Body>{item}</Body>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {data?.faq && (
                <>
                  <H3 marginTop={3} marginBottom={2}>
                    {data?.faq?.title}
                  </H3>
                  <ExpandingList items={faqListItems} />
                  <Spacing marginTop={2}>
                    <Link href={data?.faq?.viewMoreUrl} target="_blank">
                      {data?.faq?.viewMoreCopy} <Icon marginLeft icon={IconTypes.chevronRight} />
                    </Link>
                  </Spacing>
                </>
              )}
              <Body small color={theme.colors.greys.grey50} marginTop={4}>
                {data?.smallCopy}
              </Body>
            </Spacing>
          </ResponsiveCard>
        </Loading>
      </HasPermissionTo>
      <MobileCta primary large onClick={openModal} marginTop={2} marginBottom={2} load={loadSSO}>
        {data?.cta} <Icon icon={IconTypes.chevronRight} marginLeft={2} size={16} />
      </MobileCta>

      <BenefitModal isModalOpen={isModalOpen} isLoadingSecondState={isLoadingSecondState} closeModal={closeModal} onClick={onClick} />
    </ResponsiveMargin>
  )
}

export default BenefitPage

const getBenefitType = (product?: string) => {
  switch (product) {
    case "ctw":
      return VivupBenefitType.BenefitTypeCycleToWork
    case "alp":
      return VivupBenefitType.BenefitTypeAnnualLeavePurchase
    case "he":
      return VivupBenefitType.BenefitTypeHomeAndElectronics
    default: {
      const benefitType = `BENEFIT_TYPE_${product?.toUpperCase().replaceAll("-", "_")}`
      if (Object.values(VivupBenefitType).includes(benefitType as VivupBenefitType)) {
        return benefitType
      }
      return
    }
  }
}
